import React, { useState, useEffect, useRef } from 'react';
import { Save, X, GraduationCap, Church, Heart, Users, ChevronDown } from 'lucide-react';
import type { DatosComplementarios } from '../../services/memberships/memberships.service';

interface DatosComplementariosFormProps {
  initialData: DatosComplementarios | null;
  onSubmit: (data: Partial<DatosComplementarios>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const NIVELES_ESTUDIOS = [
  { value: 'primaria', label: 'Primaria' },
  { value: 'secundaria', label: 'Secundaria' },
  { value: 'tecnico', label: 'Técnico/Tecnólogo' },
  { value: 'universitario', label: 'Universitario' },
  { value: 'posgrado', label: 'Posgrado' },
];

const TIEMPOS_ASISTENCIA = [
  { value: 'menos_1_año', label: 'Menos de 1 año' },
  { value: '1_3_años', label: '1 a 3 años' },
  { value: 'mas_3_años', label: 'Más de 3 años' },
];

const TIPOS_PERSONALIDAD = [
  { value: 'extrovertido', label: 'Extrovertido' },
  { value: 'introvertido', label: 'Introvertido' },
  { value: 'cooperativo', label: 'Cooperativo' },
  { value: 'intuitivo', label: 'Intuitivo' },
];

const ESTUDIOS_OPCIONES = [
  'Consolidación',
  'Discipulado',
  'Escuela Ministerial',
  'Escuela de Predicadores',
  'Discipulado de Formación Financiera',
  'IBC (Instituto Bíblico Cuadrangular de Apartadó)',
];

const HABILIDADES_OPCIONES = [
  'Música',
  'Enseñanza',
  'Organización',
  'Comunicación',
  'Manualidades',
  'Tecnología',
  'Cocina',
  'Decoración',
  'Escritura',
  'Relaciones',
  'Danza',
];

const DONES_OPCIONES = [
  'Evangelismo',
  'Misiones',
  'Apóstol',
  'Enseñanza',
  'Exhortación',
  'Sabiduría',
  'Discernimiento',
  'Conocimiento',
  'Servicio',
  'Misericordia',
  'Hospitalidad',
  'Pastor (Pastorado)',
  'Dar (Ofrendar)',
  'Música',
  'Artes Manuales',
  'Intercesión',
  'Milagros',
  'Orar en el Espíritu (Lenguas/Interpretación)',
  'Liderazgo',
  'Administración',
  'Fe',
];

const MINISTERIOS_OPCIONES = [
  'Infantil',
  'Alabanza',
  'Red Cuidarte',
  'Discipulado',
  'Comunicaciones',
  'Edad Dorada',
  'Apoyo humanitario',
  'Danza',
  'Varones',
  'Mujeres',
  'Pastoral',
];

// Componente Dropdown Custom
interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Seleccionar...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const selectedLabel = options.find(o => o.value === value)?.label || placeholder;

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 pr-10 
                   text-xs font-medium text-gray-700 text-left
                   hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 
                   focus:border-purple-500 transition-all cursor-pointer flex items-center justify-between"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {selectedLabel}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform absolute right-3 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          <div
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
            className="px-3 py-2.5 text-xs text-gray-400 hover:bg-purple-50 hover:text-purple-700 cursor-pointer transition-colors first:rounded-t-lg"
          >
            {placeholder}
          </div>
          {options.map((option, index) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`px-3 py-2.5 text-xs font-medium cursor-pointer transition-colors
                ${value === option.value 
                  ? 'bg-purple-50 text-purple-700' 
                  : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                }
                ${index === options.length - 1 ? 'last:rounded-b-lg' : ''}
              `}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DatosComplementariosForm: React.FC<DatosComplementariosFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<Partial<DatosComplementarios>>({
    nivelEstudios: undefined,
    profesion: '',
    tiempoAsistencia: undefined,
    oracionFe: false,
    esBautizado: false,
    añoBautismo: undefined,
    iglesiaBautismo: '',
    ministroBautismo: '',
    estudiosRealizados: [],
    tipoPersonalidad: undefined,
    habilidades: [],
    dones: [],
    perteneceMinisterio: false,
    ministeriosActuales: [],
    ministerioDeseado: '',
    fechaEncuesta: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        fechaEncuesta: initialData.fechaEncuesta || new Date().toISOString().split('T')[0],
      });
    }
  }, [initialData]);

  const handleChange = (field: keyof DatosComplementarios, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field: 'estudiosRealizados' | 'habilidades' | 'dones' | 'ministeriosActuales', value: string) => {
    setFormData((prev) => {
      const current = (prev[field] || []) as string[];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Componente de Radio estilizado
  const StyledRadio = ({
    checked,
    onChange,
    label,
    name
  }: {
    checked: boolean;
    onChange: () => void;
    label: string;
    name: string;
  }) => (
    <label className="flex items-center cursor-pointer group">
      <div className="relative">
        <input
          type="radio"
          name={name}
          checked={checked}
          onChange={onChange}
          className="peer sr-only"
        />
        <div className={`
          w-5 h-5 rounded-full border-2 transition-all duration-200
          ${checked 
            ? 'border-purple-600 bg-purple-600' 
            : 'border-gray-300 bg-white group-hover:border-purple-400'
          }
        `}>
          <div className={`
            absolute inset-0 flex items-center justify-center
            transition-transform duration-200
            ${checked ? 'scale-100' : 'scale-0'}
          `}>
            <div className="w-2 h-2 rounded-full bg-white"></div>
          </div>
        </div>
      </div>
      <span className="ml-2 text-xs text-gray-700 group-hover:text-gray-900 transition-colors">
        {label}
      </span>
    </label>
  );

  // Componente de Checkbox estilizado
  const StyledCheckbox = ({
    checked,
    onChange,
    label
  }: {
    checked: boolean;
    onChange: () => void;
    label: string;
  }) => (
    <button
      type="button"
      onClick={onChange}
      className={`
        group flex items-center px-3 py-2 rounded-lg text-xs font-medium 
        transition-all duration-200 border
        ${checked
          ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
          : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300 hover:bg-purple-50'
        }
      `}
    >
      <div className={`
        w-4 h-4 rounded flex items-center justify-center mr-2 transition-all duration-200
        ${checked 
          ? 'bg-white' 
          : 'border-2 border-gray-300 group-hover:border-purple-400'
        }
      `}>
        {checked && (
          <svg className="w-3 h-3 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span className="truncate">{label}</span>
    </button>
  );

  const renderCheckboxGroup = (
    label: string,
    field: 'estudiosRealizados' | 'habilidades' | 'dones' | 'ministeriosActuales',
    options: string[],
    icon?: React.ReactNode
  ) => (
    <div className="mb-4">
      <label className="block text-xs font-medium text-gray-700 mb-3 flex items-center">
        {icon}
        <span className={icon ? 'ml-2' : ''}>{label}</span>
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = (formData[field] || []).includes(option);
          return (
            <StyledCheckbox
              key={option}
              checked={isSelected}
              onChange={() => handleArrayToggle(field, option)}
              label={option}
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Información Académica y Profesional */}
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-100">
        <h4 className="text-xs font-medium text-gray-900 mb-4 flex items-center pb-2 border-b border-gray-200">
          <GraduationCap className="w-5 h-5 mr-2 text-purple-600" />
          Información Académica y Profesional
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Nivel de Estudios
            </label>
            <CustomDropdown
              value={formData.nivelEstudios || ''}
              onChange={(value) => handleChange('nivelEstudios', value || undefined)}
              options={NIVELES_ESTUDIOS}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Profesión u Ocupación
            </label>
            <input
              type="text"
              value={formData.profesion || ''}
              onChange={(e) => handleChange('profesion', e.target.value)}
              placeholder="Ej: Administrador, Doctor, Ama de casa"
              className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg 
                         text-xs text-gray-700 placeholder-gray-400
                         focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 
                         hover:border-purple-300 transition-all duration-200"
            />
          </div>
        </div>
      </div>

      {/* Información Espiritual */}
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-100">
        <h4 className="text-xs font-medium text-gray-900 mb-4 flex items-center pb-2 border-b border-gray-200">
          <Church className="w-5 h-5 mr-2 text-purple-600" />
          Información Espiritual
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Tiempo en la Iglesia
            </label>
            <CustomDropdown
              value={formData.tiempoAsistencia || ''}
              onChange={(value) => handleChange('tiempoAsistencia', value || undefined)}
              options={TIEMPOS_ASISTENCIA}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              ¿Ha hecho la oración de fe?
            </label>
            <div className="flex items-center space-x-6 mt-2">
              <StyledRadio
                checked={formData.oracionFe === true}
                onChange={() => handleChange('oracionFe', true)}
                label="Sí"
                name="oracionFe"
              />
              <StyledRadio
                checked={formData.oracionFe === false}
                onChange={() => handleChange('oracionFe', false)}
                label="No"
                name="oracionFe"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              ¿Es Bautizado?
            </label>
            <div className="flex items-center space-x-6 mt-2">
              <StyledRadio
                checked={formData.esBautizado === true}
                onChange={() => handleChange('esBautizado', true)}
                label="Sí"
                name="esBautizado"
              />
              <StyledRadio
                checked={formData.esBautizado === false}
                onChange={() => handleChange('esBautizado', false)}
                label="No"
                name="esBautizado"
              />
            </div>
          </div>

          {formData.esBautizado && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Año de Bautismo
                </label>
                <input
                  type="number"
                  value={formData.añoBautismo || ''}
                  onChange={(e) => handleChange('añoBautismo', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Ej: 2010"
                  min="1900"
                  max={new Date().getFullYear()}
                  className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg 
                             text-xs text-gray-700 placeholder-gray-400
                             focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 
                             hover:border-purple-300 transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Iglesia de Bautismo
                </label>
                <input
                  type="text"
                  value={formData.iglesiaBautismo || ''}
                  onChange={(e) => handleChange('iglesiaBautismo', e.target.value)}
                  placeholder="Ej: Cuadrangular Central"
                  className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg 
                             text-xs text-gray-700 placeholder-gray-400
                             focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 
                             hover:border-purple-300 transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Ministro que Bautizó
                </label>
                <input
                  type="text"
                  value={formData.ministroBautismo || ''}
                  onChange={(e) => handleChange('ministroBautismo', e.target.value)}
                  placeholder="Ej: Pastor Ahimelec"
                  className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg 
                             text-xs text-gray-700 placeholder-gray-400
                             focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 
                             hover:border-purple-300 transition-all duration-200"
                />
              </div>
            </>
          )}
        </div>

        {renderCheckboxGroup('Estudios Realizados', 'estudiosRealizados', ESTUDIOS_OPCIONES)}
      </div>

      {/* Personalidad y Habilidades */}
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-100">
        <h4 className="text-xs font-medium text-gray-900 mb-4 flex items-center pb-2 border-b border-gray-200">
          <Heart className="w-5 h-5 mr-2 text-purple-600" />
          Personalidad y Habilidades
        </h4>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Tipo de Personalidad
          </label>
          <CustomDropdown
            value={formData.tipoPersonalidad || ''}
            onChange={(value) => handleChange('tipoPersonalidad', value || undefined)}
            options={TIPOS_PERSONALIDAD}
          />
        </div>

        {renderCheckboxGroup('Habilidades', 'habilidades', HABILIDADES_OPCIONES)}
        {renderCheckboxGroup('Dones Espirituales', 'dones', DONES_OPCIONES)}
      </div>

      {/* Ministerios */}
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-100">
        <h4 className="text-xs font-medium text-gray-900 mb-4 flex items-center pb-2 border-b border-gray-200">
          <Users className="w-5 h-5 mr-2 text-purple-600" />
          Participación en Ministerios
        </h4>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            ¿Pertenece actualmente a algún ministerio?
          </label>
          <div className="flex items-center space-x-6 mt-2">
            <StyledRadio
              checked={formData.perteneceMinisterio === true}
              onChange={() => handleChange('perteneceMinisterio', true)}
              label="Sí"
              name="perteneceMinisterio"
            />
            <StyledRadio
              checked={formData.perteneceMinisterio === false}
              onChange={() => handleChange('perteneceMinisterio', false)}
              label="No"
              name="perteneceMinisterio"
            />
          </div>
        </div>

        {formData.perteneceMinisterio && renderCheckboxGroup('Ministerios Actuales', 'ministeriosActuales', MINISTERIOS_OPCIONES)}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Ministerio de Interés (si no pertenece a ninguno)
          </label>
          <input
            type="text"
            value={formData.ministerioDeseado || ''}
            onChange={(e) => handleChange('ministerioDeseado', e.target.value)}
            placeholder="Ej: Alabanza, Infantil"
            className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg 
                       text-xs text-gray-700 placeholder-gray-400
                       focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 
                       hover:border-purple-300 transition-all duration-200"
          />
        </div>
      </div>

      {/* Fecha de la Encuesta */}
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-100">
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Fecha de la Encuesta
        </label>
        <input
          type="date"
          value={formData.fechaEncuesta?.split('T')[0] || ''}
          onChange={(e) => handleChange('fechaEncuesta', e.target.value)}
          className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg 
                     text-xs text-gray-700
                     focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 
                     hover:border-purple-300 transition-all duration-200"
        />
      </div>

      {/* Botones */}
      <div className="flex space-x-3 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center shadow-sm hover:shadow-md"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {initialData ? 'Actualizar Información' : 'Guardar Información'}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 bg-white text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 transition-colors font-medium flex items-center justify-center border border-gray-300 hover:border-gray-400"
        >
          <X className="w-4 h-4 mr-2" />
          Cancelar
        </button>
      </div>
    </form>
  );
};

export default DatosComplementariosForm;
